import { Card, CardBody } from '@nextui-org/react'
import { FC } from 'react'
import { useTankStore } from '../../store/tank/useTankStore'
import { useSchoolStore } from '../../store/school/useSchoolStore'
import { SchoolCard } from './SchoolCard'
import type { School } from '@/types/domain/school'

interface UngroupedCardContainerProps {
  ungroupedTankIds?: string[]
}

/**
 * UngroupedCardContainer Component
 *
 * Displays tanks that have a school (schoolId exists) but are not assigned to any group.
 * These are "Ungrouped Tanks" - tanks with fish but not in a batch/group.
 *
 * @param ungroupedTankIds - Array of tank IDs not in any group (with schoolId)
 */
export const UngroupedCardContainer: FC<UngroupedCardContainerProps> = ({
  ungroupedTankIds = []
}) => {
  const tanks = useTankStore((state) => state.tanks)
  const schools = useSchoolStore((state) => state.schools)

  // Filter to only include tanks with schoolId (ungrouped tanks)
  const unbatchedTankIds = ungroupedTankIds.filter(
    (tankId) => tankId in tanks && tanks[tankId].schoolId
  )

  const createUngroupedCards = [...unbatchedTankIds]
    .sort((a, b) => {
      const nameA = tanks[a]?.name?.toLowerCase() || ''
      const nameB = tanks[b]?.name?.toLowerCase() || ''
      return nameA.localeCompare(nameB)
    })
    .map((tankId) => {
      const tank = tanks[tankId]
      const schoolId = tank.schoolId!
      const schoolInfo = schools[schoolId]

      // SchoolInfo from store has display fields, cast to partial School for SchoolCard
      // SchoolCard only uses optional fields (averageWeight, count, totalBiomass, density, feedingRate)
      const school = schoolInfo as Partial<School> as School | undefined

      return (
        <span key={tankId}>
          <SchoolCard
            tankId={tankId}
            schoolId={schoolId}
            tank={tank}
            school={school}
          />
        </span>
      )
    })

  if (unbatchedTankIds.length === 0) return null

  return (
    <div className="mt-6 md:mt-10" data-testid="ungrouped-tanks">
      <div className="flex justify-between my-1">
        <div className="ml-1 mb-2 text-tdplindigo text-[1.25rem]">
          Ungrouped Tanks
        </div>
      </div>
      <Card
        className="bg-white border-1 border-tdpldarkgrey border-opacity-50 rounded-lg"
        shadow="none"
        radius="lg"
        style={{ borderRadius: '0.75rem' }}
      >
        <CardBody className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,16rem)] justify-start gap-3 md:gap-4 p-3 md:p-6">
          {createUngroupedCards}
        </CardBody>
      </Card>
    </div>
  )
}

export default UngroupedCardContainer
